import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';

import IdentityManager from '@arcgis/core/identity/IdentityManager';
import PortalItem from '@arcgis/core/portal/PortalItem';
import WebMap from '@arcgis/core/WebMap';
import MapView from '@arcgis/core/views/MapView';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import Geometry from '@arcgis/core/geometry/Geometry';
import Extent from '@arcgis/core/geometry/Extent';
import Point from '@arcgis/core/geometry/Point';
import Viewpoint from '@arcgis/core/Viewpoint';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import ZoomWidget from '@arcgis/core/widgets/Zoom';
import SketchWidget from '@arcgis/core/widgets/Sketch';
import ScaleBarWidget from '@arcgis/core/widgets/ScaleBar';
import Compass from '@arcgis/core/widgets/Compass';
import CustomContent from '@arcgis/core/popup/content/CustomContent';
import FieldsContent from '@arcgis/core/popup/content/FieldsContent';
import FieldInfo from '@arcgis/core/popup/FieldInfo';
import PopupTemplate from '@arcgis/core/PopupTemplate';

import { AuthService } from '../../services/auth.service';
import {
  asyncScheduler,
  catchError,
  combineLatest,
  debounceTime,
  defaultIfEmpty,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  first,
  from,
  fromEvent,
  map,
  merge,
  mergeMap,
  mergeWith,
  Observable,
  of,
  share,
  shareReplay,
  skip,
  startWith,
  Subject,
  subscribeOn,
  Subscription,
  switchMap,
  takeUntil,
  tap,
  zip,
} from 'rxjs';
import { ToastService } from '../../services/toast.service';
import { AppConfigService } from '../../services/app-config.service';
import { GoToTarget, MainMapService } from './main-map.service';
import { EsriUtils } from '../../utils/esri.utils';
import { GeolocationService } from '../../services/geolocation.service';
import { WidgetModalType } from '../../models/widget-modal-type';
import { AddressItemResponse } from '../../services/address.service';
import { LoadingController } from '../../helpers/loading.controller';

import saveFileAs from 'file-saver';
import { InitialMapConfigLike } from '../../models/auth.model';
import { InfluenceAreaComponent } from './components/influence-area/influence-area.component';
import { MapsService } from '../../services/maps.service';
import { localization } from './default-symbols';
import { ToolMapType } from '../../models/tool-map-type';
import { WidgetType } from '../../models/widget-type';
import { TocService } from './toc.service';
import { getWidgetModalLabelDictionary } from '../../constants/widget.constant';
import {
  AppTrackingService,
  TrackingToolType,
} from '../../services/app-tracking.service';
import { ToolMapActionType } from '../../models/tool-action-type';
import { HighlightService } from './highlight.service';
import { BufferService } from './buffer.service';
import { NotesService } from './notes.service';
import { ColorUtils } from '../../utils/color.utils';
import { EventBusService } from '../../services/event-bus.service';
import { OccurrenceService } from '../../services/occurrence.service';
import { SiiaAttachmentsService } from '../../services/siia-attachments.service';
import {
  geomFieldsIgnored,
  systemFieldTypesIgnored,
} from '../../constants/ignore-fields.constant';
import { onSubscribe } from '@rxjs-lifecycle/core';
import { ScreenService } from '../../services/screen.service';
import { TranslationService } from '../../services/translation.service';
import { SideMenuService } from '../../services/side-menu.service';
import {
  entityIdFieldName,
  editableFieldName,
  typeObjectFieldName,
} from '../../constants/search-results.constant';
import { QuotaWidgetConfigLike } from '../../interfaces/app-config.interface';
import { RxUtils } from '../../utils/rx.utils';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { AvailableCoverageService } from './available-coverage.service';
import { LayersInfoService } from './layers.service';
import { MapModelLike } from '../../models/map.model';
import FieldInfoFormat from '@arcgis/core/popup/support/FieldInfoFormat';
import { ArrayUtils } from '../../utils/array.utils';

interface WidgetModalItemLike {
  template: TemplateRef<any>;
  type?: WidgetModalType;
  loaded?: boolean;
  visible?: boolean;
  minimized?: boolean;
  restrictDraggingToContainerObservable?: Observable<boolean>;
  title?: string;
  width: string;
  height: string;
  maxHeight?: string;
  onClose?: Function;
  data?: any;
}

@Component({
  selector: 'app-main-map',
  templateUrl: 'main-map.component.html',
  styleUrls: ['main-map.component.scss'],
  host: {
    '[class.state-not-authenticated]': '!mapConfigured',
  },
  providers: [
    MainMapService,
    TocService,
    HighlightService,
    BufferService,
    NotesService,
    AvailableCoverageService,
  ],
})
export class MainMapComponent implements OnInit, OnDestroy {
  @ViewChild('searchByDrawingTpl', { static: true })
  searchByDrawingTpl: TemplateRef<any>;
  @ViewChild('searchResultsTpl', { static: true })
  searchResultsTpl: TemplateRef<any>;
  @ViewChild('searchResultsInternalTpl', { static: true })
  searchResultsInternalTpl: TemplateRef<any>;
  @ViewChild('loadDataFormTpl', { static: true })
  loadDataFormTpl: TemplateRef<any>;
  @ViewChild('addLayersFormTpl', { static: true })
  addLayersFormTpl: TemplateRef<any>;
  @ViewChild('quotaInfoTpl', { static: true })
  quotaInfoTpl: TemplateRef<any>;
  @ViewChild('heatmapLayersTpl', { static: true })
  heatmapLayersTpl: TemplateRef<any>;
  @ViewChild('clusterLayersTpl', { static: true })
  clusterLayersTpl: TemplateRef<any>;
  @ViewChild('measurementTpl', { static: true })
  measurementTpl: TemplateRef<any>;
  @ViewChild('registerElementMapTpl', { static: true })
  registerElementMapTpl: TemplateRef<any>;
  @ViewChild('registerEventTpl', { static: true })
  registerEventTpl: TemplateRef<any>;
  @ViewChild('mapManagementTpl', { static: true })
  mapManagementTpl: TemplateRef<any>;
  @ViewChild('calcPathTpl', { static: true })
  calcPathTpl: TemplateRef<any>;
  @ViewChild('printMapTpl', { static: true })
  printMapTpl: TemplateRef<any>;
  @ViewChild('convertCoordinatesTpl', { static: true })
  convertCoordinatesTpl: TemplateRef<any>;
  @ViewChild('notesTpl', { static: true })
  notesTpl: TemplateRef<any>;
  @ViewChild('occurrencesManagementTpl', { static: true })
  occurrencesManagementTpl: TemplateRef<any>;
  @ViewChild('searchAnnouncementTpl', { static: true })
  searchAnnouncementTpl: TemplateRef<any>;
  @ViewChild('identifyTpl', { static: true })
  identifyTpl: TemplateRef<any>;
  @ViewChild('helpTpl', { static: true })
  helpTpl: TemplateRef<any>;
  @ViewChild('availableCoverageTpl', { static: true })
  availableCoverageTpl: TemplateRef<any>;

  @ViewChild(InfluenceAreaComponent, { static: false })
  drawingSearchComp: InfluenceAreaComponent;

  @ViewChild('mapViewEl', { static: true })
  private mapViewElRef: ElementRef<HTMLDivElement>;

  @ViewChild('logoContainerEl', { static: true })
  private logoContainerElRef: ElementRef<HTMLDivElement>;

  @ViewChild('menuButtonEl', { static: true })
  private menuButtonElRef: ElementRef<HTMLDivElement>;

  @ViewChild('tocButtonEl', { static: true })
  private tocButtonElRef: ElementRef<HTMLDivElement>;

  @ViewChild('topRightFirstLineEl', { static: true })
  private topRightFirstLineElRef: ElementRef<HTMLDivElement>;

  @ViewChild('scaleInfoEl', { static: true })
  private scaleInfoEl: ElementRef<HTMLDivElement>;

  @ViewChild('scaleBarEl', { static: true })
  private scaleBarElRef: ElementRef<HTMLDivElement>;

  @ViewChild('projectionEl', { static: true })
  private projectionElRef: ElementRef<HTMLDivElement>;

  @ViewChild('overviewMapEl', { static: true })
  private overviewMapElRef: ElementRef<HTMLDivElement>;

  @ViewChild('compassEl', { static: true })
  private compassElRef: ElementRef<HTMLDivElement>;

  @ViewChild('floatingFilterContainerEl', { static: true })
  private floatingFilterContainerElRef: ElementRef<HTMLDivElement>;

  @ViewChild('popupSiiaDocumentsEl', { static: false })
  private popupSiiaDocumentsElRef: ElementRef<HTMLDivElement>;

  @ViewChild('projectionLabelEl', { static: true })
  private projectionLabelElRef: ElementRef<HTMLDivElement>;

  @ViewChild('translationButtonEl', { static: false })
  private translationButtonElRef: ElementRef<HTMLDivElement>;

  @Input()
  showLogo = true;

  @Output()
  onMapClick = new EventEmitter<any>();

  private map: WebMap;
  mapView: MapView;
  mapConfigured = false;

  get mapLoaded() {
    return !!(this.mapView && EsriUtils.isLoaded(this.mapView));
  }

  showMenuButton: boolean = false;
  isMobileScreen: boolean = false;

  private mapGroupLayers: GroupLayer;

  private availableCoverageLayer: GraphicsLayer;
  private addressLocationLayer: GraphicsLayer;
  private locationLayer: GraphicsLayer;
  searchGroupLayer: GroupLayer;
  measurementGroupLayer: GroupLayer;
  convertCoordinatesLayer: GraphicsLayer;

  notesGraphicsLayer: GraphicsLayer;

  widgetModalItemList: WidgetModalItemLike[] = [];
  widgetModalTypes = WidgetModalType;

  widgetTypes = WidgetType;

  registerElementGraphicToEdit: __esri.Graphic;
  geometryToSearch: __esri.Geometry;
  identifyPoint: __esri.Point;

  siiaGroupLayer: __esri.GroupLayer;
  private mainMapImageLayerIndex: number;
  mainMapImageLayer: __esri.MapImageLayer;
  occurrenceLayer: __esri.MapImageLayer;
  announcementLayer: __esri.MapImageLayer;

  additionalGroupLayer: __esri.GroupLayer;
  tocGroupLayer: __esri.GroupLayer;
  manipulationGroupLayer: __esri.GroupLayer;
  quotaGroupLayer: __esri.GroupLayer;

  attachmentList: { text?: string; url?: string }[];

  shareLinkModalVisibleObservable = this.mainMapService
    .onToolMapChanged(ToolMapType.ShareLink)
    .pipe(
      shareReplay({
        refCount: true,
        bufferSize: 1,
      })
    );

  private addressLocationPointSubject = new Subject<__esri.Point>();

  private lifecycle = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    public appConfig: AppConfigService,
    private authService: AuthService,
    private sideMenuService: SideMenuService,
    public mainMapService: MainMapService,
    public layersInfoService: LayersInfoService,
    public tocService: TocService,
    private highlightService: HighlightService,
    private bufferService: BufferService,
    private mapsService: MapsService,
    private notesService: NotesService,
    private availableCoverageService: AvailableCoverageService,
    private occurrenceService: OccurrenceService,
    private siiaAttachmentsService: SiiaAttachmentsService,
    private geolocationService: GeolocationService,
    private toastService: ToastService,
    private loadingController: LoadingController,
    private appTrackingService: AppTrackingService,
    private eventBus: EventBusService,
    private screenService: ScreenService,
    private translationService: TranslationService
  ) {}

  ngOnInit(): void {
    this.loadingController.show();
    combineLatest(
      this.authService.isEsriAuthenticated(),
      this.authService.getInitialMapConfig()
    )
      .pipe(
        mergeMap(([authenticated, initialMapConfig]) => {
          if (this.appConfig.loginRequired && !authenticated) {
            this.toastService.error(
              this.translationService.getCurrentLanguage()['not-authenticated']
            );
            return EMPTY;
          }

          if (authenticated) {
            return this.mapsService.getFavoriteMap().pipe(
              catchError(() => {
                this.toastService.error(
                  this.translationService.getCurrentLanguage()[
                    'failed-load-favorite-map'
                  ]
                );

                return of(null);
              }),
              mergeMap((favoriteMap) => {
                if (this.appConfig.siiaMode) {
                  return this.authService.getEsriCredentials().pipe(
                    map((esriCredentials) => {
                      IdentityManager.registerToken(esriCredentials);

                      return favoriteMap;
                    })
                  );
                } else {
                  return of(favoriteMap);
                }
              }),
              tap((favoriteMap) => {
                this.setupMap(initialMapConfig, favoriteMap);
              })
            );
          } else {
            const mapId = this.route.snapshot.queryParamMap.get('mapId');
            if (mapId) {
              return this.mapsService.getMapById(mapId).pipe(
                catchError((response) => {
                  if (response instanceof HttpErrorResponse) {
                    if (response.status === HttpStatusCode.NotFound) {
                      this.toastService.error(
                        this.translationService.getCurrentLanguage()[
                          'map-not-found'
                        ]
                      );

                      return of(null);
                    }
                  }

                  this.toastService.error(
                    this.translationService.getCurrentLanguage()[
                      'failed-load-map'
                    ]
                  );

                  return of(null);
                }),
                tap((currentMap) => {
                  this.setupMap(null, currentMap);
                })
              );
            } else {
              this.setupMap();

              return EMPTY;
            }
          }
        }),
        finalize(() => {
          this.loadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe();

    this.mainMapService.onRequestClear
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        this.geometryToSearch = null;
        this.identifyPoint = null;
      });

    this.eventBus
      .on('register-element.edit-start')
      .pipe(takeUntil(this.lifecycle))
      .subscribe((e) => {
        this.registerElementGraphicToEdit = e;
      });

    this.screenService
      .observeIsMobile()
      .pipe(takeUntil(this.lifecycle))
      .subscribe((isMobile) => {
        this.isMobileScreen = isMobile;
        this.showMenuButton = isMobile;
      });
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();

    this.mapView?.destroy();
    this.map?.destroy();

    this.widgetModalItemList = null;
  }

  onBtnMenuToggleClick() {
    this.sideMenuService.toggle();
  }

  handleTocModal() {
    this.tocService.toggle();
  }

  onAddressLocationChange(item: AddressItemResponse) {
    this.mainMapService.disableAllSingleTools();
    this.addressLocationLayer?.removeAll();

    if (item) {
      const point = new Point(item.location);
      this.addPointToMap(this.addressLocationLayer, point);

      this.addressLocationPointSubject.next(point);

      const extent = new Extent(item.extent);

      this.mainMapService.goTo(extent);
      this.appTrackingService.send({
        type: TrackingToolType.Widget,
        name: 'Ir para',
        action: true,
      });
    }
  }

  onWidgetModalHidden(item: WidgetModalItemLike) {
    this.mainMapService.closeWidgetModal(item.type);

    const index = this.widgetModalItemList.indexOf(item);
    if (index > -1) {
      this.widgetModalItemList.splice(index, 1);
    }

    item.onClose?.();
    item.loaded = false;
  }

  onResultClearClick() {
    this.drawingSearchComp?.reset();
  }

  onSearchByGeometry(geom: __esri.Geometry) {
    this.geometryToSearch = geom;

    if (geom) {
      this.mainMapService.openWidgetModal(WidgetModalType.AdvancedSearch);
    }
  }

  onFilterOnMapClick() {
    this.sideMenuService.close();
  }

  isWidgetActive(type: WidgetType) {
    return this.mainMapService.isWidgetActive(type);
  }

  private setupMap(
    initialMapConfig?: InitialMapConfigLike,
    mapToLoad?: MapModelLike
  ) {
    this.layersInfoService
      .getPortal(this.appConfig.portalUrl)
      .pipe(takeUntil(this.lifecycle))
      .subscribe((portal) => {
        this.mapConfigured = true;

        const portalItem = new PortalItem({
          id: this.appConfig.mapId,
          portal,
        });

        this.map = new WebMap({
          portalItem,
        });

        this.mapView = new MapView({
          container: this.mapViewElRef.nativeElement,
          map: this.map,
        });

        if (initialMapConfig) {
          const center = initialMapConfig.center;
          if (
            typeof center?.latitude === 'number' &&
            typeof center?.longitude === 'number'
          ) {
            this.mapView.center = new Point(center);

            if (typeof initialMapConfig.scale === 'number') {
              this.mapView.scale = initialMapConfig.scale;
            }
          }
        }

        this.mapGroupLayers = new GroupLayer();

        this.setupMapLayers();
        this.setupMapWidgets();

        this.loadingController.show();
        EsriUtils.when(this.mapView)
          .pipe(
            finalize(() => {
              this.loadingController.hide();
            }),
            takeUntil(this.lifecycle)
          )
          .subscribe(() => {
            this.map.add(this.mapGroupLayers);

            this.setupMapEvents();
          });

        if (mapToLoad) {
          this.mainMapService.loadMap(mapToLoad);
        }
      });
  }

  private setupMapWidgets() {
    const widgetKeys = this.mapView.ui.components.slice();
    for (const key of widgetKeys) {
      this.mapView.ui.remove(key);
    }

    this.mapView.ui.add(this.logoContainerElRef.nativeElement, 'top-left');

    this.mapView.ui.add(this.topRightFirstLineElRef.nativeElement, 'top-right');

    this.mapView.ui.add(
      this.projectionLabelElRef.nativeElement,
      'bottom-right'
    );

    this.mapView.ui.add(this.projectionElRef.nativeElement, 'bottom-right');

    this.mapView.ui.add(this.scaleBarElRef.nativeElement, 'bottom-right');

    this.mapView.ui.add(
      this.floatingFilterContainerElRef.nativeElement,
      'bottom-left'
    );

    this.mapView.ui.add(this.overviewMapElRef.nativeElement, 'bottom-left');

    const scaleBar = new ScaleBarWidget({
      container: this.scaleInfoEl.nativeElement,
      view: this.mapView,
      style: 'line',
      unit: 'metric',
    });
    this.mapView.ui.add(this.scaleInfoEl.nativeElement, 'bottom-left');
    combineLatest(
      this.observeWidgetMapChanges(WidgetType.Scales),
      this.screenService.observeIsMobile()
    )
      .pipe(
        switchMap(([active, isMobile]) => {
          scaleBar.visible = active && !isMobile;
          if (!scaleBar.visible) {
            return EMPTY;
          }

          const el = this.scaleInfoEl.nativeElement;
          return RxUtils.observeElementResize(el).pipe(
            map(([entry]) => {
              const borderBoxSize = entry.borderBoxSize[0];
              return borderBoxSize.inlineSize;
            }),
            startWith(el.getBoundingClientRect().width),
            distinctUntilChanged(),
            tap((width) => {
              const margin = Math.max(0, 176 - width);
              el.style.setProperty('margin-right', `${margin}px`);
            })
          );
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe();

    const compass = new Compass({
      container: this.compassElRef.nativeElement,
      view: this.mapView,
    });

    this.mapView.ui.add(compass, 'top-right');

    compass.when(() => {
      const btn = compass.container as HTMLElement;
      if (btn) {
        btn.title =
          this.translationService.getCurrentLanguage()['reset-map-orientation'];
        btn.setAttribute(
          'aria-label',
          this.translationService.getCurrentLanguage()['reset-map-orientation']
        );
      }
    });

    this.mapView.ui.add(this.tocButtonElRef.nativeElement, 'top-right');

    if (this.appConfig.externalMode) {
      this.mapView.ui.add(
        this.translationButtonElRef?.nativeElement,
        'top-right'
      );
    }

    this.mapView.ui.add(this.menuButtonElRef.nativeElement, 'top-left');
  }

  private setupMapLayers() {
    if (this.appConfig.siiaMode) {
      this.siiaGroupLayer = new GroupLayer();
      this.mapGroupLayers.add(this.siiaGroupLayer);

      this.tocService
        .onSiiaLayersCreated()
        .pipe(takeUntil(this.lifecycle))
        .subscribe((e) => {
          this.mainMapImageLayer = e?.mainMapImageLayer;
          this.occurrenceLayer = e?.occurrenceLayer;
          this.announcementLayer = e?.announcementLayer;

          this.setupPopupSiia();

          if (this.mainMapImageLayer) {
            this.mainMapImageLayerIndex = this.siiaGroupLayer.layers.indexOf(
              this.mainMapImageLayer
            );

            this.mainMapService.mainImageLayerVisibilityChanges
              .pipe(takeUntil(this.lifecycle))
              .subscribe((value) => {
                if (value) {
                  this.siiaGroupLayer.add(
                    this.mainMapImageLayer,
                    this.mainMapImageLayerIndex
                  );
                } else {
                  this.siiaGroupLayer.remove(this.mainMapImageLayer);
                }
              });
          }
        });
    }

    this.tocGroupLayer = new GroupLayer();
    this.mapGroupLayers.add(this.tocGroupLayer);

    this.additionalGroupLayer = new GroupLayer({
      title: this.translationService.getCurrentLanguage()['external-layers'],
      visible: false,
    });

    this.mapGroupLayers.add(this.additionalGroupLayer);

    this.manipulationGroupLayer = new GroupLayer();
    this.mapGroupLayers.add(this.manipulationGroupLayer);

    this.quotaGroupLayer = new GroupLayer();
    this.mapGroupLayers.add(this.quotaGroupLayer);

    this.searchGroupLayer = new GroupLayer();
    this.mapGroupLayers.add(this.searchGroupLayer);

    this.bufferService.setup(this.mapGroupLayers);
    this.highlightService.setup(this.mapGroupLayers);

    this.notesGraphicsLayer = new GraphicsLayer();
    this.mapGroupLayers.add(this.notesGraphicsLayer);

    this.notesService.setup(this.notesGraphicsLayer);

    this.availableCoverageLayer = new GraphicsLayer();
    this.mapGroupLayers.add(this.availableCoverageLayer);

    this.addressLocationLayer = new GraphicsLayer();
    this.mapGroupLayers.add(this.addressLocationLayer);

    this.locationLayer = new GraphicsLayer();
    this.mapGroupLayers.add(this.locationLayer);

    this.convertCoordinatesLayer = new GraphicsLayer();
    this.mapGroupLayers.add(this.convertCoordinatesLayer);

    this.measurementGroupLayer = new GroupLayer();
    this.mapGroupLayers.add(this.measurementGroupLayer);

    this.tocService.load(
      this.mapView,
      this.tocGroupLayer,
      this.siiaGroupLayer,
      this.additionalGroupLayer
    );
  }

  private setupMapEvents() {
    EsriUtils.leftImmediateClicks(this.mapView)
      .pipe(takeUntil(this.lifecycle))
      .subscribe((e) => {
        this.onMapClick.emit(e);
      });

    this.setupRotationEvents();
    this.setupFullscreenEvents();
    this.setupZoomByWindow();
    this.setupZoomEvents();
    this.setupGoToEvents();
    this.setupViewpointEvents();
    this.setupSelectMultiLayersEvents();
    this.setupCurrentLocationEvents();
    this.setupStreetViewEvents();
    this.setupScreenshotEvents();
    this.setupIndentifyEvents();
    this.setupAvailableCoverageEvents();
    this.setupWidgetModalEvents();
    this.setupNotesEvents();
  }

  private setupWidgetModalEvents() {
    this.mainMapService.onWidgetModalToggle
      .pipe(takeUntil(this.lifecycle))
      .subscribe(({ type, opened }) => {
        const index = this.widgetModalItemList.findIndex(
          (x) => x.type === type
        );
        const item = this.widgetModalItemList[index];

        if (!opened) {
          if (item) {
            if (item.visible) {
              item.visible = false;
            }

            if (!item.loaded) {
              this.widgetModalItemList.splice(index, 1);
            }
          }
        } else {
          if (item) {
            item.visible = true;
            return;
          }

          this.enableWidgetModal(type);
        }
      });

    for (const type of this.mainMapService.widgetModalActiveList) {
      this.enableWidgetModal(type);
    }
  }

  private setupPopupSiia() {
    const popup = this.mapView.popup;
    popup.autoOpenEnabled = false;

    this.mainMapService.mapPopupEnabledChanges
      .pipe(
        switchMap((enabled) => {
          if (!enabled) {
            return EMPTY;
          }

          return this.observeMapLeftClicks().pipe(
            switchMap((e: __esri.ViewClickEvent) => {
              return new Observable((subscriber) => {
                popup.location = e.mapPoint;
                popup.spinnerEnabled = true;

                const clientRect =
                  this.mapViewElRef.nativeElement.getBoundingClientRect();
                const point = this.mapView.toMap({
                  x: e.x,
                  y: e.y - clientRect.bottom / 2 + 140,
                });

                let fetchSubscription: Subscription;
                subscriber.add(() => {
                  fetchSubscription?.unsubscribe();

                  popup.clear();
                  popup.close();
                });

                popup.promises = [
                  EsriUtils.fetchFeaturesByPopup(popup, e)
                    .pipe(
                      mergeMap((res) => {
                        if (!res.allGraphicsPromise) {
                          return EMPTY;
                        }

                        return from(res.allGraphicsPromise).pipe(
                          map((x) => {
                            return x?.[x?.length - 1];
                          }),
                          filter((x) => !!x)
                        );
                      }),
                      finalize(() => {
                        popup.spinnerEnabled = false;
                      }),
                      onSubscribe((subscription) => {
                        fetchSubscription = subscription;
                      }),
                      tap((graphic) => {
                        popup.open({
                          features: [graphic],
                        });

                        this.mainMapService.goTo(
                          new Viewpoint({
                            targetGeometry: point,
                            scale: this.mapView.scale,
                          })
                        );

                        subscriber.add(
                          EsriUtils.watch(popup, 'visible')
                            .pipe(map((e) => e.newValue))
                            .subscribe((visible) => {
                              if (!visible) {
                                subscriber.complete();
                              }
                            })
                        );

                        subscriber.add(
                          this.highlightService
                            .highlight(graphic.geometry)
                            .subscribe()
                        );
                      })
                    )
                    .toPromise(),
                ];
              });
            })
          );
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe();

    this.loadingController.show();
    zip(
      [this.mainMapImageLayer, this.occurrenceLayer, this.announcementLayer]
        .filter((x) => !!x)
        .map((layer) => {
          return EsriUtils.loadAllLayers(layer).pipe(
            catchError(() => {
              return EMPTY;
            }),
            map(() => {
              return layer;
            })
          );
        })
    )
      .pipe(
        defaultIfEmpty([] as __esri.Layer[]),
        finalize(() => {
          this.loadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe((layers) => {
        const fieldNamesToIgnore = [
          editableFieldName,
          typeObjectFieldName,
          entityIdFieldName,
        ];

        for (const layer of layers) {
          let getAttachmentsFn: (
            graphic: __esri.Graphic
          ) => Observable<{ text?: string; url?: string }[]>;

          let otherOutFieldNames: string[];

          switch (layer) {
            case this.mainMapImageLayer:
              otherOutFieldNames = ['identidade', 'cdt'];

              getAttachmentsFn = (graphic: __esri.Graphic) => {
                const entityId = graphic.attributes['identidade'];
                const cdt = graphic.attributes['cdt'];
                return this.siiaAttachmentsService.getAttachmentsFromRegisterElementCDT(
                  entityId,
                  cdt
                );
              };
              break;
            case this.announcementLayer:
              otherOutFieldNames = ['idsiia'];

              getAttachmentsFn = (graphic: __esri.Graphic) => {
                const idsiia = graphic.attributes['idsiia'];
                return this.siiaAttachmentsService.getAttachmentsFromAnnouncementId(
                  idsiia
                );
              };
              break;
            case this.occurrenceLayer:
              otherOutFieldNames = ['id_ocorrencia'];

              getAttachmentsFn = (graphic: __esri.Graphic) => {
                const occurrenceId = graphic.attributes['id_ocorrencia'];
                return this.occurrenceService.listAttachmentsByOccurrenceId(
                  occurrenceId
                );
              };
              break;
          }

          let fetchAttachmentsSubscription: Subscription;
          if (getAttachmentsFn) {
            let getAttachmentsFnAux = getAttachmentsFn;
            getAttachmentsFn = (x) => {
              return getAttachmentsFnAux(x).pipe(
                onSubscribe((subscription) => {
                  fetchAttachmentsSubscription = subscription;
                }),
                tap((list) => {
                  this.attachmentList = list;
                }),
                takeUntil(this.lifecycle)
              );
            };
          }

          const sublayers = EsriUtils.getDeeperLayers(layer) as Array<
            __esri.FeatureLayer | __esri.Sublayer
          >;
          for (let layerAux of sublayers) {
            layerAux.popupEnabled = true;

            const popupTemplate = layerAux.popupTemplate ?? new PopupTemplate();
            layerAux.popupTemplate = popupTemplate;

            popupTemplate.title = layerAux.title ?? layerAux.id.toString();

            const fieldInfos = layerAux.fields
              .filter((field) => {
                return (
                  !fieldNamesToIgnore.includes(field.name) &&
                  !geomFieldsIgnored.includes(field.name) &&
                  !systemFieldTypesIgnored.includes(field.type)
                );
              })
              .map((field) => {
                let fieldInfo = popupTemplate.fieldInfos?.find(
                  (x) => x.fieldName === field.name
                );

                if (!fieldInfo) {
                  fieldInfo = new FieldInfo({
                    fieldName: field.name,
                    label: field.alias ?? field.name,
                  });
                }

                if (field.type === 'date' && !fieldInfo.format) {
                  fieldInfo.format = new FieldInfoFormat({
                    dateFormat: 'day-short-month-year',
                  });
                }

                return fieldInfo;
              });

            let outFields = fieldInfos.map((x) => x.fieldName);
            if (otherOutFieldNames?.length) {
              outFields = outFields.concat(otherOutFieldNames);
              outFields = ArrayUtils.uniqBy(outFields);
            }

            popupTemplate.outFields = outFields;

            popupTemplate.fieldInfos = fieldInfos;
            popupTemplate.content = [
              new FieldsContent({
                fieldInfos,
              }),
              new CustomContent({
                creator: (async ({ graphic }: { graphic: __esri.Graphic }) => {
                  if (getAttachmentsFn) {
                    await getAttachmentsFn(graphic).toPromise();
                  }

                  return this.popupSiiaDocumentsElRef?.nativeElement;
                }) as any,
                destroyer: () => {
                  fetchAttachmentsSubscription?.unsubscribe();
                },
              }),
            ];
          }
        }
      });
  }

  private async enableWidgetModal(type: WidgetModalType) {
    let widgetItem: WidgetModalItemLike;

    switch (type) {
      case WidgetModalType.LoadData:
        widgetItem = {
          template: this.loadDataFormTpl,
          width: '40rem',
          height: '33rem',
        };
        break;
      case WidgetModalType.AddLayer:
        widgetItem = {
          template: this.addLayersFormTpl,
          width: '27.6rem',
          height: '20.9rem',
        };
        break;
      case WidgetModalType.HeatMap:
        widgetItem = {
          template: this.heatmapLayersTpl,
          width: '24rem',
          height: 'auto',
          maxHeight: '45.6rem',
        };
        break;
      case WidgetModalType.ClusterMap:
        widgetItem = {
          template: this.clusterLayersTpl,
          width: '24rem',
          height: 'auto',
          maxHeight: '45.6rem',
        };
        break;
      case WidgetModalType.CoverageQuota:
        widgetItem = this.buildQuotaWidget(this.appConfig.coverageQuotaConfig, {
          columnAlias: 'Objeto cadastro',
          initialMsg: 'Clique no Mapa para obter a Quota de Cobertura Nacional',
        });
        break;
      case WidgetModalType.PropertyQuota:
        widgetItem = this.buildQuotaWidget(this.appConfig.propertyQuotaConfig, {
          columnAlias: 'Entidade',
          initialMsg: 'Clique no Mapa para obter a Quota de Propriedade',
        });
        break;
      case WidgetModalType.SearchByDrawing:
        widgetItem = {
          template: this.searchByDrawingTpl,
          width: '26rem',
          height: 'auto',
          onClose: () => {
            if (
              !this.mainMapService.isWidgetModalActive(
                WidgetModalType.AdvancedSearch
              )
            ) {
              this.geometryToSearch = null;
            }
          },
        };
        break;
      case WidgetModalType.AdvancedSearch:
        widgetItem = {
          template: !this.appConfig.siiaMode
            ? this.searchResultsInternalTpl
            : this.searchResultsTpl,
          width: '87.4rem',
          height: '49rem',
          onClose: () => {
            if (
              !this.mainMapService.isWidgetModalActive(
                WidgetModalType.SearchByDrawing
              )
            ) {
              this.geometryToSearch = null;
            }
          },
        };
        break;
      case WidgetModalType.Measurement:
        widgetItem = {
          template: this.measurementTpl,
          width: '30rem',
          height: 'auto',
        };
        break;
      case WidgetModalType.RegisterElement:
        widgetItem = {
          template: this.registerElementMapTpl,
          width: '27.7rem',
          height: 'auto',
          onClose: () => {
            this.registerElementGraphicToEdit = null;
          },
        };
        break;
      case WidgetModalType.ConvertCoordinate:
        widgetItem = {
          template: this.convertCoordinatesTpl,
          width: '39.5rem',
          height: 'auto',
          maxHeight: '66.7rem',
        };
        break;
      case WidgetModalType.MapManagement:
        widgetItem = {
          template: this.mapManagementTpl,
          width: '50rem',
          height: 'auto',
        };
        break;
      case WidgetModalType.PrintMap:
        widgetItem = {
          template: this.printMapTpl,
          width: '27.7rem',
          height: 'auto',
        };
        break;
      case WidgetModalType.Occurrence:
        widgetItem = {
          template: this.occurrencesManagementTpl,
          width: '58.9rem',
          height: '44rem',
        };
        break;
      case WidgetModalType.RegisterEvent:
        widgetItem = {
          template: this.registerEventTpl,
          width: '58.9rem',
          height: '44rem',
        };
        break;
      case WidgetModalType.Announcements:
        widgetItem = {
          template: this.searchAnnouncementTpl,
          width: '58.9rem',
          height: '44rem',
        };
        break;
      case WidgetModalType.Notes:
        widgetItem = {
          template: this.notesTpl,
          title: 'Anotações',
          width: '30rem',
          height: 'auto',
          maxHeight: '44rem',
          onClose: () => {
            this.notesService.setNotes([]);
          },
        };
        break;
      case WidgetModalType.Identify:
        widgetItem = {
          template: this.identifyTpl,
          width: '49rem',
          height: '39rem',
          onClose: () => {
            this.identifyPoint = null;
          },
        };
        break;
      case WidgetModalType.Path:
        widgetItem = {
          template: this.calcPathTpl,
          width: '22.4rem',
          height: '60rem',
        };
        break;
      case WidgetModalType.Help:
        widgetItem = {
          template: this.helpTpl,
          width: this.appConfig.widgetHelpSize?.width ?? '87.4rem',
          height: this.appConfig.widgetHelpSize?.height ?? '49rem',
          maxHeight: this.appConfig.widgetHelpSize?.maxHeight ?? 'auto',
        };
        break;
      case WidgetModalType.AvailableCoverage:
        widgetItem = {
          template: this.availableCoverageTpl,
          width: '55.9rem',
          height: '37rem',
          onClose: () => {
            this.availableCoverageService.setPoint(null);
            this.availableCoverageLayer?.removeAll();
          },
        };
        break;
    }

    if (widgetItem) {
      widgetItem.title = getWidgetModalLabelDictionary(
        this.translationService
      ).get(type);
      widgetItem.visible = true;
      widgetItem.type = type;

      const widgetItemRef = new WeakRef(widgetItem);
      widgetItem.data = Object.assign(widgetItem.data ?? {}, {
        closeWidget: () => {
          widgetItemRef.deref().visible = false;
        },
        maximizeWidget: () => {
          widgetItemRef.deref().minimized = false;
        },
        minimizeWidget: () => {
          widgetItemRef.deref().minimized = true;
        },
      });

      const { mainMapService } = this;

      Object.defineProperty(widgetItem, 'minimized', {
        get: () => {
          return mainMapService.isWidgetModalMinimized(type);
        },
        set: (value) => {
          if (value) {
            mainMapService.minimizeWidgetModal(type);
          } else {
            mainMapService.maximizeWidgetModal(type);
          }
        },
      });

      widgetItem.restrictDraggingToContainerObservable =
        this.mainMapService.onWidgetModalMinimized.pipe(
          filter((x) => x.type === type),
          map((x) => !!x.minimized),
          startWith(widgetItem.minimized),
          distinctUntilChanged(),
          debounceTime(100)
        );

      this.widgetModalItemList.push(widgetItem);
    }
  }

  private buildQuotaWidget(
    config: QuotaWidgetConfigLike,
    additionalOptions: {
      columnAlias: string;
      initialMsg: string;
    }
  ): WidgetModalItemLike {
    return {
      template: this.quotaInfoTpl,
      width: '27rem',
      height: '43.1rem',
      data: {
        config,
        columnAlias: additionalOptions.columnAlias,
        initialMsg: additionalOptions.initialMsg,
      },
    };
  }

  private setupNotesEvents() {
    this.mainMapService.onNotesLoad
      .pipe(takeUntil(this.lifecycle))
      .subscribe((notes) => {
        this.notesService.setNotes(notes);
      });
  }

  private setupFullscreenEvents() {
    const toolMapType = ToolMapType.Fullscreen;

    const isDocumentInFullscreen = () => !!document.fullscreenElement;

    this.mainMapService
      .onToolMapChanged(toolMapType)
      .pipe(
        switchMap((active) => {
          if (active && !isDocumentInFullscreen()) {
            document.body.requestFullscreen();

            return fromEvent(document, 'fullscreenchange').pipe(
              subscribeOn(asyncScheduler),
              map(isDocumentInFullscreen),
              tap((enabled) => {
                if (
                  !enabled &&
                  this.mainMapService.isToolMapActive(toolMapType)
                ) {
                  this.mainMapService.disableToolMap(toolMapType);
                }
              })
            );
          } else if (isDocumentInFullscreen()) {
            document.exitFullscreen();
          }

          return EMPTY;
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe();
  }

  private setupZoomByWindow() {
    const toolMapType = ToolMapType.ZoomByWindow;
    this.mainMapService
      .onToolMapChanged(toolMapType)
      .pipe(
        switchMap((active) => {
          if (!active) {
            return EMPTY;
          }

          return new Observable((subscriber) => {
            const sketchToZoomLayer = new GraphicsLayer();
            this.map.add(sketchToZoomLayer);

            const sketch = new SketchWidget({
              view: this.mapView,
              layer: sketchToZoomLayer,
              creationMode: 'single',
            });

            const color = ColorUtils.rgbToHex('rgb(210, 105, 30)');
            sketch.viewModel.activeFillSymbol = new SimpleFillSymbol({
              style: 'solid',
              outline: {
                color,
                width: 1,
                style: 'solid',
              },
            });

            subscriber.add(
              EsriUtils.on(sketch, 'create').subscribe(
                (evt: __esri.SketchCreateEvent) => {
                  switch (evt.state) {
                    case 'cancel':
                    case 'complete':
                      if (evt.graphic?.geometry) {
                        this.mainMapService.goTo(evt.graphic.geometry);
                      }

                      sketchToZoomLayer.removeAll();
                      this.mainMapService.disableToolMap(toolMapType);
                      break;
                  }
                }
              )
            );

            sketch.create('rectangle');

            subscriber.add(() => {
              sketch.cancel();
              sketch.destroy();

              this.map.remove(sketchToZoomLayer);
              sketchToZoomLayer.destroy();
            });
          });
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe();
  }

  private setupRotationEvents() {
    const toolMapType = ToolMapType.Rotation;
    this.mainMapService
      .onToolMapChanged(toolMapType)
      .pipe(takeUntil(this.lifecycle))
      .subscribe((active) => {
        this.mapView.constraints.rotationEnabled = active;
      });
  }

  private setupZoomEvents() {
    const zoomWidget = new ZoomWidget({
      view: this.mapView,
    });

    const refreshCanZoom = () => {
      this.mainMapService.canZoomIn = zoomWidget.viewModel.canZoomIn;
      this.mainMapService.canZoomOut = zoomWidget.viewModel.canZoomOut;
    };

    EsriUtils.watch(this.mapView, 'zoom')
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        refreshCanZoom();
      });

    this.mainMapService.onZoomIn
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        zoomWidget.zoomIn();
      });

    this.mainMapService.onZoomOut
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        zoomWidget.zoomOut();
      });
  }

  private setupGoToEvents() {
    const handleTarget = (target: GoToTarget) => {
      if (Array.isArray(target)) {
        target = (target as any[]).filter((x: Geometry | Graphic) => {
          if (x instanceof Graphic) {
            x = x.geometry;
          }

          return !!x;
        });

        if (target.length === 1) {
          target = target[0];
        }
      }

      if (!target || (Array.isArray(target) && !target.length)) {
        return null;
      }

      if (
        target instanceof Geometry &&
        target.type === 'point' &&
        !target.extent
      ) {
        target = new Viewpoint({
          targetGeometry: target,
          scale: 4514,
        });
      }

      return target;
    };

    this.mainMapService.onGoTo
      .pipe(
        switchMap(({ target, options }) => {
          target = handleTarget(target);
          if (!target) {
            return EMPTY;
          }

          return EsriUtils.goTo(this.mapView, target, options);
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe();
  }

  private setupViewpointEvents() {
    this.mainMapService.onViewpointChanged
      .pipe(
        startWith(this.mainMapService.getCurrentViewpoint()),
        filter((x) => !!x),
        takeUntil(this.lifecycle)
      )
      .subscribe((viewpoint) => {
        if (EsriUtils.isViewpointEquals(viewpoint, this.mapView.viewpoint)) {
          return;
        }

        this.mainMapService.goTo(viewpoint);
      });

    const animationObservable = EsriUtils.watch(this.mapView, 'animation').pipe(
      share()
    );

    const navigatingObservable = EsriUtils.watch(
      this.mapView,
      'navigating'
    ).pipe(share());

    merge(
      navigatingObservable.pipe(
        filter((x) => x.newValue),
        switchMap(() => {
          return navigatingObservable.pipe(
            first((x) => !x.newValue),
            takeUntil(animationObservable.pipe(first()))
          );
        })
      ),
      animationObservable.pipe(filter((x) => !x.newValue && x.oldValue?.done))
    )
      .pipe(
        map(() => this.mapView.viewpoint),
        startWith(
          this.mainMapService.getCurrentViewpoint() ?? this.mapView.viewpoint
        ),
        distinctUntilChanged((lastViewpoint, currentViewpoint) => {
          return EsriUtils.isViewpointEquals(lastViewpoint, currentViewpoint);
        }),
        filter((x) => !!x),
        takeUntil(this.lifecycle)
      )
      .subscribe((viewpoint) => {
        this.mainMapService.setViewpoint(viewpoint);
      });
  }

  private setupSelectMultiLayersEvents() {
    if (!this.appConfig.siiaMode) {
      return;
    }

    const toolMapType = ToolMapType.Identify;
    this.mainMapService
      .onToolMapChanged(toolMapType)
      .pipe(
        switchMap((isActive) => {
          if (!isActive) {
            return EMPTY;
          }

          this.mainMapService.disableMapPopup();
          return new Observable<any>((subscriber) => {
            subscriber.add(() => {
              this.mainMapService.enableMapPopup();
            });

            const toast = this.toastService.info(
              this.translationService.getCurrentLanguage().warning,
              this.translationService.getCurrentLanguage()['select-location']
            );

            subscriber.add(
              toast.onDismiss().subscribe(() => {
                this.mainMapService.disableToolMap(toolMapType);
              })
            );

            subscriber.add(
              this.observeMapLeftClicks()
                .pipe(
                  map((e) => e.mapPoint as __esri.Point),
                  first(),
                  finalize(() => {
                    toast.dismiss();
                  })
                )
                .subscribe((point) => {
                  this.mainMapService.closeWidgetModal(
                    WidgetModalType.SearchByDrawing
                  );

                  this.geometryToSearch = EsriUtils.geodesicBuffer(point, 5);
                  this.mainMapService.openWidgetModal(
                    WidgetModalType.AdvancedSearch
                  );
                })
            );
          });
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe();
  }

  private setupCurrentLocationEvents() {
    const type = ToolMapType.CurrentLocation;
    this.mainMapService
      .onToolMapChanged(type)
      .pipe(
        switchMap((active) => {
          if (!active) {
            return EMPTY;
          }

          let first = false;
          return this.geolocationService.watchCurrentLocation().pipe(
            catchError(() => {
              this.toastService.error(
                this.translationService.getCurrentLanguage()[
                  'failed-load-location'
                ]
              );

              this.mainMapService.disableToolMap(type);

              return EMPTY;
            }),
            tap((location) => {
              const point = new Point({
                latitude: location.coords.latitude,
                longitude: location.coords.longitude,
              });

              this.locationLayer?.removeAll();
              this.addPointToMap(this.locationLayer, point);

              if (!first) {
                first = true;
                this.mainMapService.goTo(point);
              }
            }),
            finalize(() => {
              this.locationLayer?.removeAll();
            })
          );
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe();
  }

  private setupStreetViewEvents() {
    const toolMapType = ToolMapType.StreetView;
    this.mainMapService
      .onToolMapChanged(toolMapType)
      .pipe(
        switchMap((isActive) => {
          if (!isActive) {
            return EMPTY;
          }

          return new Observable<any>((subscriber) => {
            const toast = this.toastService.info(
              this.translationService.getCurrentLanguage().warning,
              this.translationService.getCurrentLanguage()['select-location']
            );

            subscriber.add(
              toast.onDismiss().subscribe(() => {
                this.mainMapService.disableToolMap(toolMapType);
              })
            );

            subscriber.add(
              this.observeMapLeftClicks()
                .pipe(
                  map((e) => e.mapPoint as __esri.Point),
                  finalize(() => {
                    toast.dismiss();
                  })
                )
                .subscribe(({ latitude, longitude }) => {
                  this.mainMapService.openStreetMapWindow(latitude, longitude);
                })
            );
          });
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe();
  }

  private setupScreenshotEvents() {
    this.mainMapService.onRequestToolAction
      .pipe(
        filter(
          (x) =>
            x === ToolMapActionType.Screenshot &&
            !this.loadingController.isShown()
        ),
        mergeMap(() => {
          this.loadingController.show();

          const format = 'png';
          return EsriUtils.takeScreenshotAsBlob(this.mapView, {
            format,
            quality: 5,
          }).pipe(
            finalize(() => {
              this.loadingController.hide();
            }),
            catchError((err) => {
              this.toastService.error(
                this.translationService.getCurrentLanguage()[
                  'failed-export-image'
                ]
              );

              return EMPTY;
            }),
            tap((blob) => {
              const filename = `mapa-${new Date()
                .toLocaleDateString()
                .replaceAll('/', '-')}.${format}`;

              saveFileAs(blob, filename);
            })
          );
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe();
  }

  private setupIndentifyEvents() {
    if (this.appConfig.siiaMode) {
      return;
    }

    const identifyModeClass = 'identify-mode';
    const toolMapType = ToolMapType.Identify;
    const toolMapChangedObservable = this.mainMapService
      .onToolMapChanged(toolMapType)
      .pipe(
        shareReplay({
          refCount: true,
          bufferSize: 1,
        })
      );

    toolMapChangedObservable
      .pipe(takeUntil(this.lifecycle))
      .subscribe((active) => {
        if (active) {
          this.mapViewElRef.nativeElement?.classList?.add(identifyModeClass);
        } else {
          this.mapViewElRef.nativeElement?.classList?.remove(identifyModeClass);
        }
      });

    this.mainMapService
      .onToolMapChanged()
      .pipe(
        startWith(null),
        map(
          () =>
            !this.mainMapService.hasAnyToolMapActive() ||
            this.mainMapService.isToolMapActive(toolMapType)
        ),
        switchMap((allowed) => {
          if (!allowed) {
            return EMPTY;
          }

          return combineLatest(
            this.screenService.observeIsMobile(),
            toolMapChangedObservable
          ).pipe(
            switchMap(([isMobile, active]) => {
              if (isMobile || active) {
                if (!active) {
                  return EMPTY;
                }

                return this.observeMapLeftClicks();
              } else {
                return this.observeMapRightClicks();
              }
            })
          );
        }),
        map((e) => e.mapPoint as __esri.Point),
        takeUntil(this.lifecycle)
      )
      .subscribe((point) => {
        this.identifyPoint = point as any;
        this.mainMapService.openWidgetModal(WidgetModalType.Identify);

        this.mainMapService.disableToolMap(ToolMapType.Identify);
      });
  }

  private setupAvailableCoverageEvents() {
    if (this.appConfig.siiaMode) {
      return;
    }

    this.availableCoverageService.loadingChanges
      .pipe(skip(1), takeUntil(this.lifecycle))
      .subscribe((visible) => {
        if (visible) {
          this.loadingController.show();
        } else {
          this.loadingController.hide();
        }
      });

    const toolMapType = ToolMapType.AvailableCoverage;
    this.mainMapService
      .onToolMapChanged(toolMapType)
      .pipe(
        switchMap((isActive) => {
          if (!isActive) {
            return this.addressLocationPointSubject.pipe(
              tap(() => {
                this.availableCoverageService.setPoint(null);
              })
            );
          }

          this.availableCoverageService.setPoint(null);

          return new Observable<any>((subscriber) => {
            const toast = this.toastService.info(
              this.translationService.getCurrentLanguage().warning,
              this.translationService.getCurrentLanguage()[
                'select-location-or-enter-address'
              ]
            );

            subscriber.add(
              toast.onDismiss().subscribe(() => {
                this.mainMapService.disableToolMap(toolMapType);
              })
            );

            subscriber.add(
              this.observeMapLeftClicks()
                .pipe(
                  map((e) => e.mapPoint as __esri.Point),
                  mergeWith(this.addressLocationPointSubject),
                  first(),
                  finalize(() => {
                    toast.dismiss();
                  })
                )
                .subscribe((point) => {
                  this.availableCoverageService.setPoint(point);

                  this.addressLocationLayer?.removeAll();
                  this.availableCoverageLayer?.removeAll();
                  this.addPointToMap(this.availableCoverageLayer, point);
                })
            );
          });
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe();
  }

  private addPointToMap(layer: __esri.GraphicsLayer, point: Point) {
    const graphic = new Graphic({
      geometry: point,
      symbol: localization.pointSymbolDefault,
    });

    layer?.add(graphic);
  }

  private observeWidgetMapChanges(type: WidgetType) {
    return this.mainMapService.onWidgetToggle.pipe(
      filter((x) => x.type === type),
      map((x) => x.active),
      startWith(this.mainMapService.isWidgetActive(type)),
      distinctUntilChanged(),
      debounceTime(0),
      takeUntil(this.lifecycle)
    );
  }

  private observeMapRightClicks() {
    return EsriUtils.rightClicks(this.mapView);
  }

  private observeMapLeftClicks() {
    return EsriUtils.leftClicks(this.mapView);
  }
}
